import React, { useEffect } from 'react';
import { navbarStyles } from './navbar.styles';
import {
    Button, Input, Tabs, message,
} from 'antd';
import DocumentTitle from 'react-document-title';
import { ReduxStore } from 'reducers/redux.types';
import GenericHoc from './generic-hoc';
import { HocOptions } from './generic-hoc.types';
import Loader from './Loader';
import ConsignmentsIcon from 'assets/consignments';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import Helper from 'library/Helper';
import { useTranslation } from 'react-i18next';
import { Routes } from 'library/constants';
import ConsignmentDetails from 'components/consignments/consignment-details-new';
import { fetchConsignmentTracking, printConsignment } from 'network/consignments.api';
import SingleConsignmentNew from 'components/create-consignment/single-cn-creation/single-consignment-new';
import { loadConsignments } from 'actions/consignment-actions';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { CreateActions } from 'components/create-consignment/create-modal.constants';
import SuccessPopup from 'components/create-consignment/success-popup';

const MobileTrackTabContent = ({
    value,
    onChange,
    placeholder,
    onTrack,
    onCancel,
    trackDisabled,
    t,
    classes,
    error,
    isLoading,
}: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder: string;
    onTrack: () => void;
    onCancel: () => void;
    trackDisabled: boolean;
    t: (key: string) => string;
    classes: any;
    error?: string;
    isLoading?: boolean;
}) => (
    isLoading ? <div style={{ height: '100px' }}><Loader /></div> : (
        <div style={{ padding: 16 }}>
            <Input
                value={value}
                onChange={onChange}
                addonBefore={<SearchOutlined />}
                placeholder={placeholder}
                autoFocus
                className={classes.trackConsignmentMobile}
            />
            {error && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        fontSize: 12,
                        color: '#f5222d',
                    }}
                >
                    {error}
                </div>
            )}
            <div
                style={{
                    paddingTop: 12,
                    display: 'flex',
                    gap: 8,
                    borderTop: '1px solid #e8e8e8',
                }}
            >
                <Button
                    size="large"
                    type="primary"
                    onClick={onTrack}
                    disabled={trackDisabled}
                    style={{
                        borderRadius: 6,
                        flexGrow: 1,
                        fontWeight: 600,
                    }}
                >
                    {t('Track')}
                </Button>
                <Button
                    size="large"
                    onClick={onCancel}
                    style={{
                        borderRadius: 6,
                        flexGrow: 1,
                    }}
                >
                    {t('Cancel')}
                </Button>
            </div>
        </div>
    )
);

const tabConfigs = [
    {
        key: 'consignment_number',
        label: 'Consignment No',
        placeholder: 'track_consignment',
        icon: <ConsignmentsIcon />,
    },
    {
        key: 'customer_reference_number',
        label: '# Reference No',
        placeholder: 'track_customer_reference_number',
    },
];
interface NavbarMobileProps {
    classes: any;
    routesInfo: Record<string, { title: string }>;
    loading: boolean;
    trackQuery: string;
    setTrackQuery: (query: string) => void;
    trackType: string;
    setTrackType: (type: string) => void;
    pageTitle: string;
    history: any;
    master: any;
    loadconsignments: () => void;
}

const NavbarMobile: React.FC<NavbarMobileProps> = (props: NavbarMobileProps) => {
    const {
        classes, routesInfo, loading,
        trackType, setTrackType, trackQuery,
        setTrackQuery,
        pageTitle, history, master,
        loadconsignments,
    } = props;
    const enable_customer_config = master?.parts_to_show?.enable_customer_config || false;
    const useThirdPartyLabel = enable_customer_config ? master?.properties?.use_third_party_label : false;
    const { t } = useTranslation();
    const currentRoute = Helper.getCurrentRoute();
    const [mobileTrackDropdownVisible, setMobileTrackDropdownVisible] = React.useState(false);
    const [referenceNumber, setReferenceNumber] = React.useState('');
    const [error, setError] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [showConsignmentForm, setShowConsignmentForm] = React.useState(false);
    const [successVisible, setSuccessVisible] = React.useState<any>({});

    const getConginmentDetailsUrl = (ref: string) => {
        return `/${Routes.CONSIGNMENT_DETAILS}/${ref}?view=customer_portal_all`;
    };

    const goToDetailsPage = (ref: any) => {
        history.push(getConginmentDetailsUrl(ref));
    };

    useEffect(() => {
        if (referenceNumber) {
            goToDetailsPage(referenceNumber);
        }
    }, [referenceNumber]);

    const handleTrack = async () => {
        if (!trackQuery) return;
        setIsLoading(true);
        const response = await fetchConsignmentTracking(
            { [trackType === 'consignment_number' ? 'referenceNumber' : 'customerReferenceNumber']: trackQuery },
        );
        if (response.isSuccess && response.data?.reference_number) {
            setReferenceNumber(response.data.reference_number);
        } else {
            setError(response.errorMessage || 'Unable to fetch consignment details');
        }
        setIsLoading(false);
    };

    const renderMobileTrackDropdown = () => {
        if (!mobileTrackDropdownVisible) return null;

        return (
            <>
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.4)',
                        zIndex: 1000,
                    }}
                    onClick={() => {
                        setTrackQuery('');
                        setError('');
                        setReferenceNumber('');
                        setIsLoading(false);
                        setMobileTrackDropdownVisible(false);
                    }}
                />
                <div
                    style={{
                        position: 'fixed',
                        top: 60,
                        left: 0,
                        right: 0,
                        zIndex: 1001,
                        background: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                >
                    <Tabs
                        activeKey={trackType}
                        onChange={(key) => {
                            setTrackType(key);
                            setTrackQuery('');
                            setError('');
                        }}
                        className={classes.mobileTrackTabs}
                    >
                        {tabConfigs.map((tab) => (
                            <Tabs.TabPane
                                tab={
                                    (
                                        <span>
                                            {tab.icon}
                                            &nbsp;
                                            {t(tab.label)}
                                        </span>
                                    )
                                }
                                key={tab.key}
                                style={{ width: '100%' }}
                            >
                                <MobileTrackTabContent
                                    value={trackType === tab.key ? trackQuery || '' : ''}
                                    onChange={(e) => {
                                        setTrackQuery(e.target.value);
                                        setError('');
                                    }}
                                    placeholder={t(tab.placeholder)}
                                    onTrack={() => handleTrack()}
                                    onCancel={() => {
                                        setMobileTrackDropdownVisible(false);
                                        setTrackQuery('');
                                    }}
                                    trackDisabled={!trackQuery}
                                    t={t}
                                    classes={classes}
                                    error={error}
                                    isLoading={isLoading}
                                />
                            </Tabs.TabPane>
                        ))}
                    </Tabs>
                </div>
            </>
        );
    };

    const renderConsignmentDetailed = () => {
        if (!referenceNumber) {
            return null;
        }
        return (
            <ConsignmentDetails
                referenceNumber={referenceNumber}
                details={referenceNumber}
                master={master}
                handleGoback={() => {
                    setReferenceNumber('');
                    history.goBack();
                }}
                showErrorMessage={setError}
            />
        );
    };

    const handlePrintLabel = (fileBuffer: any, fileName: string) => {
        if (fileBuffer.isSuccess) {
            const name = fileBuffer.filename || fileName;
            Helper.downloadFileData(
                fileBuffer.data,
                name,
                true,
                'pdf',
            );
            message.success('Success');
        } else {
            message.error('Failed to generate PDF!');
        }
    };

    const handlePrint = async (referenceNumbers: any, isSmall: boolean, fileName: string) => {
        const fileBuffer = await printConsignment({
            isSmall,
            referenceNumbers,
            useThirdPartyLabel,
        });
        handlePrintLabel(fileBuffer, fileName);
    };

    const handleProtipActions = (action: string) => {
        if (action === 'close') {
            setSuccessVisible({});
        }
        if (action === 'print') {
            const refNumber = successVisible.data.referenceNumber;
            handlePrint([refNumber], true, `Shipping_Label_${refNumber}.pdf`);
        }
        if (action === 'addMore') {
            setShowConsignmentForm(true);
            setSuccessVisible({});
        }
    };

    const handleCreation = (action: CreateActions, data: any) => {
        setShowConsignmentForm(false);
        setSuccessVisible({
            action,
            data,
            creationTime: moment().format('DD MMM YYYY | hh:mm A'),
        });
    };

    const renderConsignmentForm = () => {
        if (!showConsignmentForm) return null;

        const isConfigEnabled = master?.config?.customer_portal_order_creation_config?.is_enabled;
        if (!isConfigEnabled) return null;

        return (
            <SingleConsignmentNew
                visible={showConsignmentForm}
                onClose={() => setShowConsignmentForm(false)}
                setSuccess={(data: any) => {
                    handleCreation(CreateActions.SingleConsignment, data);
                }}
                loadconsignments={loadconsignments}
                master={master}
            />
        );
    };

    const renderSuccessPopup = () => {
        if (!successVisible.action) {
            return null;
        }
        return (
            <SuccessPopup
                action={CreateActions.SingleConsignment}
                data={successVisible.data}
                creationTime={successVisible.creationTime}
                handleAction={handleProtipActions}
                showSupplementaryServices={false}
            />
        );
    };

    return (
        <div className={classes.mainDiv}>
            {loading && <Loader zIndex={10} />}
            <DocumentTitle title={pageTitle} />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 10px',
                    width: '100%',
                }}
            >
                <div className={classes.pageTitle} style={{ fontSize: 16 }}>
                    {t(routesInfo[currentRoute]?.title)}
                </div>
                <div className={classes.navbarButtonGroup}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            const isConfigEnabled = master?.config?.customer_portal_order_creation_config?.is_enabled;
                            if (isConfigEnabled) {
                                setShowConsignmentForm(true);
                            } else {
                                message.info('Consignment creation is not enabled', 1);
                            }
                        }}
                        className={classes.navbarButton}
                    >
                        Add
                    </Button>
                    <Button
                        type="primary"
                        className={classes.navbarButton}
                        onClick={() => setMobileTrackDropdownVisible(true)}
                    >
                        Track
                    </Button>
                </div>
            </div>
            {renderMobileTrackDropdown()}
            {!isLoading && renderConsignmentDetailed()}
            {renderConsignmentForm()}
            {renderSuccessPopup()}
        </div>
    );
};


const mapStateToProps = (state: ReduxStore) => {
    const { master, miscellaneousState } = state;
    return {
        routesInfo: miscellaneousState?.ROUTES_INFO || {},
        master,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    const actions = {
        loadconsignments: loadConsignments,
    };
    return bindActionCreators(actions, dispatch);
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: navbarStyles,
    },
    connectRouter: true,
};

export default GenericHoc(hocConfig)(NavbarMobile);
